import {noop, pick} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {useStore, ReactFlowState, Transform} from 'reactflow';

import {assert} from 'Asserts';
import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  ComplexPolymerType,
  SequenceEntityGraphQL,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {AxisOrientation, StaticAxis, getWidthForBaseCount} from 'modbio/polymer-maps/OligoMap';
import Type from 'molbio/maps/Type';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {
  ModifiedNucleotidesLegendContents,
  ModifiedNucleotidesLegendContentsFromItems,
} from 'molbio/monomers/ModifiedNucleotidesLegend';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {NucleotideAxisData} from '../PolymerStructureGraphConstants';
import {getOligoDuplexLegendItems} from '../tabs/OligoDuplexPolymerMap';
import {useComplexPolymerDragAndDropCanvasContext} from './ComplexPolymerDragAndDropContext';
import {getxFn} from './ComplexPolymerDragAndDropUtil';

function transformSelector(state: ReactFlowState): Transform {
  return state.transform;
}

export interface NucleotideAxisProps extends NucleotideAxisData {
  complexPolymerType: ComplexPolymerType;
}

export function NucleotideAxis({
  complexPolymerType,
  position,
  basesLength,
  isForward,
}: NucleotideAxisProps): JSX.Element {
  const transform = useStore(transformSelector);

  const type = new Type(20, 22);

  const basesWidth = getWidthForBaseCount(basesLength);
  const xFn = getxFn(0, basesLength, 0, basesWidth, isForward);

  let axisYTransform = 3;
  let axisOrientation = AxisOrientation.BOTTOM;
  if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX && isForward) {
    axisOrientation = AxisOrientation.TOP;
    axisYTransform = 20;
  }

  return (
    <svg
      style={{
        color: Color.GRAY_500,
        fill: Color.GRAY_500,
        position: 'absolute',
        left: position.x,
        top: position.y,
        transformOrigin: `${-position.x}px ${-position.y}px`,
        width: basesWidth,
        height: '100%',
        transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`,
      }}
    >
      <g id="axis" transform={`translate(3,${axisYTransform})`}>
        <StaticAxis
          basesLength={basesLength}
          charWidth={type.charWidth()}
          xFn={xFn}
          vMargin={0}
          tickSpacing={2}
          color={Color.GRAY_500}
          axisOrientation={axisOrientation}
          isReverse={!isForward}
        />
      </g>
    </svg>
  );
}

interface ModifiedNucleotidesLegendProps {
  complexPolymerType: ComplexPolymerType;
  backbonePolymers: Array<SimplePolymerGraphQL>;
}

export function ModifiedNucleotidesLegend({
  complexPolymerType,
  backbonePolymers,
}: ModifiedNucleotidesLegendProps): JSX.Element {
  if (complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE) {
    assert(backbonePolymers.length === 1, 'Expected exactly one backbone polymer for oligo conjugate');
    const {bases, modifiedNucleotides, nucleotideType} = backbonePolymers[0].entity as SequenceEntityGraphQL;

    return (
      <ModifiedNucleotidesLegendContents
        seqMods={modifiedNucleotides}
        bases={bases}
        nucleotideType={nucleotideType}
        sequenceType={SequenceType.oligo}
        onUpdateHoverState={noop}
      />
    );
  }

  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={({refetch}) => null}>
      {({monomers}) => {
        const forwardOligoEntity = backbonePolymers[0].entity as SequenceEntityGraphQL;
        const forwardOligo = pick(forwardOligoEntity, 'bases', 'modifiedNucleotides', 'nucleotideType');
        const reverseOligoEntity = backbonePolymers[1].entity as SequenceEntityGraphQL;
        const reverseOligo = pick(reverseOligoEntity, 'bases', 'modifiedNucleotides', 'nucleotideType');
        const uniqueLegendItems = getOligoDuplexLegendItems(forwardOligo, reverseOligo, monomers);
        return (
          <ModifiedNucleotidesLegendContentsFromItems
            legendItems={uniqueLegendItems}
            onUpdateHoverState={noop}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface EditToggleButtonProps {
  isEditable: boolean;
}

export function EditToggleButton({isEditable}: EditToggleButtonProps): JSX.Element {
  const {isEditing, setIsEditing} = useComplexPolymerDragAndDropCanvasContext();

  return (
    <div
      className={css(styles.toggle)}
      style={{
        background: isEditing ? Color.BLUE_500 : Color.GRAY_50,
      }}
      onClick={() => {
        if (isEditable) {
          setIsEditing(!isEditing);
        }
      }}
    >
      <div style={{marginTop: isEditing ? 0 : 15, marginBottom: isEditing ? 15 : 0}}>
        <Button
          icon="edit"
          buttonStyle={ButtonStyle.TERTIARY_WHITE}
          size={ButtonSize.MINI}
          disabled={!isEditable}
        />
      </div>
    </div>
  );
}

interface WarningBannerProps {
  className: string;
  message: string;
}

export function WarningBanner({className, message}: WarningBannerProps): JSX.Element {
  return (
    <div className={className}>
      <InlineBanner bannerType={InlineBannerType.WARNING} text={message} />
    </div>
  );
}

const styles = StyleSheet.create({
  toggle: {
    padding: 2,
    borderRadius: 5,
    zIndex: 5,
    cursor: 'grab',
  },
});
